import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../utils/api";
import { toast } from "react-toastify";
import { CircularProgress } from "@mui/material";
import ConfirmDialog from "../components/ConfirmDialog";
import {
  Box,
  Card,
  CardContent,
  Avatar,
  Typography,
  Switch,
  Divider,
  Button,
  IconButton,
  Stack,
  Autocomplete,
  TextField,
  useMediaQuery,
  Alert,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import EditIcon from "@mui/icons-material/Edit";
import useAuth from "../hooks/useAuth";
import DisabledButtonWrapper from "../components/DisabledButtonWrapper";
import { displayLeadCategoryLabel } from "../utils/leadCategoryLabel";
import { getUserInitials } from "../utils/userInitials";


export default function EditUserPage() {
  // ✅ USER ID FROM ROUTE
  const { id: userId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [showPassword, setShowPassword] = useState(false);
  const [assignToTerritory, setAssignToTerritory] = useState(false);
  const [selectedTerritories, setSelectedTerritories] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [groups, setGroups] = useState([]);
  const [isProfileEditMode, setIsProfileEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [profileData, setProfileData] = useState({
    fullName: "",
    username: "",
    password: "",
    mobile: "",
    email: "",
    location: "",
  });
  const [permissionStates, setPermissionStates] = useState({
    "Admin": false,
    "Sales Director": false,
    "Manager": false,
    "Sales Rep": false,
  });
  const [platformSettings, setPlatformSettings] = useState({
    isActive: true,
    sendSystemEmails: true,
    sendSystemSMS: false,
  });
  // ✅ Get current user's permissions
  const { can, role, isReadOnly: currentUserIsReadOnly } = useAuth();
  // ✅ Add read-only state (boolean, not group-based) - this is for the user being edited
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [canViewAdditionalNotes, setCanViewAdditionalNotes] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [adminExists, setAdminExists] = useState(false);
  const [showAdminWarning, setShowAdminWarning] = useState(false);
  const [showProtectedAdminRoleWarning, setShowProtectedAdminRoleWarning] = useState(false);
  const [confirmSalesDirector, setConfirmSalesDirector] = useState(false);
  const [pendingSalesDirectorChange, setPendingSalesDirectorChange] = useState(false);
  const [users, setUsers] = useState([]);
  // ✅ Add territory validation error state
  const [territoryError, setTerritoryError] = useState("");
  const [leadCategories, setLeadCategories] = useState([]);
  const [selectedLeadCategoryIds, setSelectedLeadCategoryIds] = useState([]);
  const [fillQuickUserId, setFillQuickUserId] = useState("");
  const [fillQuickApiKey, setFillQuickApiKey] = useState("");
  const [hasStoredFillQuickApiKey, setHasStoredFillQuickApiKey] = useState(false);
  const [showStoredFillQuickMask, setShowStoredFillQuickMask] = useState(false);

  const canManageLeadCategoryAccess = role === "Admin" || role === "Sales Director";
  const targetUserIsAdmin = permissionStates["Admin"] === true;
  const adminSettingsLockedMessage = "Admin settings cannot be changed";

const ROLE_GROUP_ALIASES = {
  "Admin": ["Admin"],
  "Sales Director": ["Sales Director"],
  "Manager": ["Manager", "Territory Manager", "Regional Manager"],
  "Sales Rep": ["Sales Rep"],
};

const findRoleGroup = (groupsList, roleName) => {
  const aliases = ROLE_GROUP_ALIASES[roleName] || [roleName];
  return (groupsList || []).find((g) =>
    aliases.some(
      (alias) =>
        (g?.name || "").trim().toLowerCase() === alias.trim().toLowerCase()
    )
  );
};


  // Fetch user data
  useEffect(() => {

    const fetchUser = async () => {
      if (!userId) return;
      
      setLoading(true);
      try {
        const response = await API.get(`/users/${userId}/`);

        const userData = response.data;

        
        setUser(userData);
        
        // Set platform settings from user data
        setPlatformSettings({
          isActive: userData.is_active !== false, // Default to true if not set
          sendSystemEmails: userData.send_system_emails !== false, // Read from API, default to true
          sendSystemSMS: false, // Not in API, default to false
        });
        
        // Update profile data with fetched user data
        setProfileData({
          fullName: userData.full_name || `${userData.first_name || ""} ${userData.last_name || ""}`.trim() || userData.username || "",
          username: userData.username || "",
          password: "", // Don't fetch password
          mobile: "", // Not in API response, will need to be added
          email: userData.email || "",
          location: "", // Not in API response, will need to be added
        });

        // Territory assignment will be set in a separate useEffect
        // that runs when both user and territories are available

        // Map user groups to permission sliders
        if (userData.groups) {
          const userGroupNames = Array.isArray(userData.groups)
            ? userData.groups.map(g => typeof g === 'object' ? g.name : g)
            : [];
          
          const hasManagerGroup =
            userGroupNames.includes("Manager") ||
            userGroupNames.includes("Territory Manager") ||
            userGroupNames.includes("Regional Manager");

          setPermissionStates({
            "Admin": userGroupNames.includes("Admin"),
            "Sales Director": userGroupNames.includes("Sales Director"),
            "Manager": hasManagerGroup,
            "Sales Rep": userGroupNames.includes("Sales Rep"),
          });
        }
        
        // ✅ Load is_read_only from API response (boolean field)
        setIsReadOnly(userData.is_read_only || false);
        setCanViewAdditionalNotes(userData.can_view_additional_notes !== false);
        setFillQuickUserId(userData.fillquick_user_id || "");
        setFillQuickApiKey("");
        const keyConfigured = !!userData.fillquick_configured;
        setHasStoredFillQuickApiKey(keyConfigured);
        setShowStoredFillQuickMask(keyConfigured);
      } catch (error) {
        console.error("Error fetching user:", error);
        toast.error("Failed to load user data");
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  // Admin accounts cannot be read-only; clear local toggle when role is Admin
  useEffect(() => {
    if (permissionStates["Admin"]) {
      setIsReadOnly(false);
      setCanViewAdditionalNotes(true);
      setAssignToTerritory(false);
      setSelectedLeadCategoryIds([]);
      setPlatformSettings((prev) => ({
        ...prev,
        isActive: true,
        sendSystemEmails: true,
      }));
    }
  }, [permissionStates["Admin"]]);

  // Helper function to normalize group names/objects to IDs
  const normalizeToIds = (valueList, groupsList) => {
    const arr = Array.isArray(valueList) ? valueList : valueList ? [valueList] : [];
    const ids = arr
      .map((v) => {
        if (typeof v === "number" && Number.isFinite(v)) return v;
        if (typeof v === "string") {
          const n = Number(v);
          if (Number.isFinite(n)) return n;
          const byName = groupsList.find((g) => g.name === v);
          if (byName) return byName.id;
          const byIdString = groupsList.find((g) => String(g.id) === v);
          if (byIdString) return byIdString.id;
          return null;
        }
        if (v && typeof v === "object") {
          if (typeof v.id === "number") return v.id;
        }
        return null;
      })
      .filter((id) => typeof id === "number" && Number.isFinite(id));
    return Array.from(new Set(ids));
  };

  // 🔹 Check if admin exists
  const checkAdminExists = (usersList) => {
    const hasAdmin = usersList.some(user => {
      if (!user.groups || !Array.isArray(user.groups)) return false;
      
      // Check if user has Admin role (exclude current user being edited)
      const userGroupNames = user.groups.map(g => 
        typeof g === 'object' ? g.name : g
      );
      return userGroupNames.includes("Admin") && user.id !== parseInt(userId);
    });
    
    setAdminExists(hasAdmin);
    return hasAdmin;
  };

  // Handle save button click
  const handleSave = async () => {
    if (currentUserIsReadOnly) return; // Kill the click for read-only users
    
    if (!user) return;

    // ✅ Clear previous errors
    setTerritoryError("");

    try {
      // Parse fullName into first_name and last_name
      const nameParts = profileData.fullName.trim().split(/\s+/);
      const first_name = nameParts[0] || "";
      const last_name = nameParts.slice(1).join(" ") || "";

      // Get current groups from permission states
      const activeRole = Object.keys(permissionStates).find(
        key => permissionStates[key]
      );
      const roleGroup = activeRole ? findRoleGroup(groups, activeRole) : null;
      const groupIds = roleGroup ? [roleGroup.id] : [];

      // Get territory IDs from selected territories
      // If slider is OFF, clear territory. If ON, use selected territory
      let territoryIds = assignToTerritory ? selectedTerritories.map(t => t.id) : [];
      
      // ✅ FIX: If user is Sales Rep or Manager, preserve existing territories if toggle is off
      const requiresTerritories = activeRole === "Sales Rep" || activeRole === "Manager";
      
      if (requiresTerritories && territoryIds.length === 0) {
        // Preserve existing territories from user object
        if (user.territories && Array.isArray(user.territories) && user.territories.length > 0) {
          territoryIds = user.territories.map(ut => 
            typeof ut === 'object' ? ut.id : ut
          ).filter(id => id != null);
        }
        
        // If still no territories, show on-page error
        if (territoryIds.length === 0) {
          setTerritoryError("Sales Reps and Managers must be assigned at least one territory.");
          // Scroll to error
          setTimeout(() => {
            const errorElement = document.getElementById('territory-error');
            if (errorElement) {
              errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 100);
          return;
        }
      }
      
      const territory_id = territoryIds.length > 0 ? territoryIds[0] : null;

      // Build payload
      const editedUserIsAdmin = permissionStates["Admin"];

      const payload = {
        first_name,
        last_name,
        username: profileData.username,
        email: profileData.email,
        is_active: editedUserIsAdmin ? true : platformSettings.isActive,
        is_read_only: editedUserIsAdmin ? false : isReadOnly,
        can_view_additional_notes: editedUserIsAdmin ? true : canViewAdditionalNotes,
        send_system_emails: editedUserIsAdmin ? true : platformSettings.sendSystemEmails,
        fillquick_user_id: fillQuickUserId,
        groups: groupIds,
         territories: territoryIds,
  	primary_territory: territoryIds.length > 0 ? territoryIds[0] : null,

  	// ⏳ TEMP legacy support
  	territory_id: territoryIds.length > 0 ? territoryIds[0] : null,
      };

      // Only include password if it was changed (not empty)
      if (profileData.password && profileData.password.trim()) {
        payload.password = profileData.password;
      }
      if (fillQuickApiKey && fillQuickApiKey.trim()) {
        payload.fillquick_api_key = fillQuickApiKey.trim();
      }

      // Update user in backend
      await API.patch(`/users/${userId}/`, payload);
      if (fillQuickApiKey && fillQuickApiKey.trim()) {
        setHasStoredFillQuickApiKey(true);
        setShowStoredFillQuickMask(true);
        setFillQuickApiKey("");
      } else if (hasStoredFillQuickApiKey) {
        setShowStoredFillQuickMask(true);
      }
      if (canManageLeadCategoryAccess) {
        await API.put(`/users/${userId}/lead-category-access/`, {
          allowed_category_ids: selectedLeadCategoryIds,
        });
      }

      // Update local user state
      const updatedUser = {
        ...user,
        first_name,
        last_name,
        username: profileData.username,
        email: profileData.email,
        is_active: editedUserIsAdmin ? true : platformSettings.isActive,
        is_read_only: editedUserIsAdmin ? false : isReadOnly,
        can_view_additional_notes: editedUserIsAdmin ? true : canViewAdditionalNotes,
        send_system_emails: editedUserIsAdmin ? true : platformSettings.sendSystemEmails,
        groups: roleGroup ? [roleGroup] : [],
        
	territories: territoryIds,  // ✅ Add this
  	primary_territory: territoryIds.length > 0 ? territoryIds[0] : null,  // ✅ Add this

      };
      setUser(updatedUser);
      

      // Exit edit mode
      setIsProfileEditMode(false);

      toast.success("User saved successfully");
    } catch (error) {
      console.error("Error saving user:", error);
      
      // ✅ Handle backend validation errors
      if (error.response?.data?.territories) {
        const errorMsg = Array.isArray(error.response.data.territories) 
          ? error.response.data.territories[0] 
          : error.response.data.territories;
        setTerritoryError(errorMsg);
        // Scroll to error
        setTimeout(() => {
          const errorElement = document.getElementById('territory-error');
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100);
      } else {
        toast.error(error.response?.data?.detail || "Failed to save user");
      }
    }
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    if (currentUserIsReadOnly) return; // Kill the click for read-only users
    
    if (!user) return;
    
    try {
      await API.delete(`/users/${userId}/`);
      toast.success("User deleted successfully");
      navigate("/users"); // Navigate back to users page
    } catch (error) {
      console.error("Error deleting user:", error);
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  // Handle permission slider change - mutually exclusive
  const handlePermissionChange = async (roleName, checked) => {
    if (currentUserIsReadOnly) return; // Kill the click for read-only users
    
    if (!user || !groups.length) return;

    if (targetUserIsAdmin) {
      setShowProtectedAdminRoleWarning(true);
      return;
    }

    // Find the group ID for this role
    const roleGroup = findRoleGroup(groups, roleName);
    if (!roleGroup) {
      toast.error(`Role "${roleName}" not found`);
      return;
    }

    // 🔹 Admin warning check
    if (roleName === "Admin" && checked) {
      // Check if admin already exists (excluding current user)
      if (adminExists) {
        setShowAdminWarning(true);
        return; // Don't proceed with the change
      }
    }

    // 🔹 Sales Director confirmation
    if (roleName === "Sales Director" && checked) {
      // Show confirmation dialog when turning ON
      setPendingSalesDirectorChange(true);
      setConfirmSalesDirector(true);
      return; // Don't proceed yet, wait for confirmation
    }

    try {
      // If turning ON: set only this role (mutually exclusive)
      // If turning OFF: set empty groups array
      const newGroupIds = checked ? [roleGroup.id] : [];
      	
      // Update permission states (mutually exclusive)
      const newStates = {
        "Admin": false,
        "Sales Director": false,
        "Manager": false,
        "Sales Rep": false,
      };
      if (checked) {
        newStates[roleName] = true;
      }
      setPermissionStates(newStates);

      const territoryIds = selectedTerritories.map(t => t.id);

      // Update user in backend
      const payload = {
        groups: newGroupIds,
        territories: territoryIds,
	primary_territory: territoryIds.length > 0 ? territoryIds[0] : null,
      };

      await API.patch(`/users/${userId}/`, payload);
      
      // Update local user state
      setUser(prev => ({
        ...prev,
        groups: checked ? [roleGroup] : [],
	territories: territoryIds,
	primary_territory: territoryIds.length > 0 ? territoryIds[0] : null,
      }));

      toast.success(`User role updated to ${roleName}`);
    } catch (error) {
      console.error("Error updating user role:", error);
      toast.error("Failed to update user role");
      
      // Revert permission states on error
      if (user.groups) {
        const userGroupNames = Array.isArray(user.groups)
          ? user.groups.map(g => typeof g === 'object' ? g.name : g)
          : [];
        setPermissionStates({
          "Admin": userGroupNames.includes("Admin"),
          "Sales Director": userGroupNames.includes("Sales Director"),
          "Manager":
            userGroupNames.includes("Manager") ||
            userGroupNames.includes("Territory Manager") ||
            userGroupNames.includes("Regional Manager"),
          "Sales Rep": userGroupNames.includes("Sales Rep"),
        });
      }
    }
  };

  // Handle read-only toggle change
  const handleReadOnlyChange = (checked) => {
    if (currentUserIsReadOnly) return; // Kill the click for read-only users
    if (permissionStates["Admin"]) return;

    setIsReadOnly(checked);
  };

  const handleCanViewAdditionalNotesChange = (checked) => {
    if (currentUserIsReadOnly || permissionStates["Admin"]) return;
    setCanViewAdditionalNotes(checked);
  };

  // Handle Sales Director confirmation
  const handleConfirmSalesDirector = async () => {
    if (!user || !groups.length) return;
    
    const roleGroup = groups.find(g => g.name === "Sales Director");
    if (!roleGroup) return;

    try {
      const newGroupIds = [roleGroup.id];
      const territoryIds = selectedTerritories.map(t => t.id);
      // Update permission states
      const newStates = {
        "Admin": false,
        "Sales Director": true,
        "Manager": false,
        "Sales Rep": false,
      };
      setPermissionStates(newStates);

      // Update user in backend
      const payload = {
      groups: newGroupIds,
      territories: territoryIds,
      primary_territory: territoryIds.length > 0 ? territoryIds[0] : null,
    };

      await API.patch(`/users/${userId}/`, payload);
      
      // Update local user state
       setUser(prev => ({
      ...prev,
      groups: [roleGroup],
      territories: territoryIds,
      primary_territory: territoryIds.length > 0 ? territoryIds[0] : null,
    }));

      toast.success("User role updated to Sales Director");
    } catch (error) {
      console.error("Error updating user role:", error);
      toast.error("Failed to update user role");
    } finally {
      setConfirmSalesDirector(false);
      setPendingSalesDirectorChange(false);
    }
  };

  // Fetch territories
  useEffect(() => {
    const fetchTerritories = async () => {
      try {
        const response = await API.get("/territories/");
        setTerritories(response.data || []);
      } catch (error) {
        console.error("Error fetching territories:", error);
      }
    };
    fetchTerritories();
  }, []);

  // Set selected territory when both user and territories are available
useEffect(() => {
  if (!user || territories.length === 0) return;
  if (selectedTerritories.length > 0) return;
  if (permissionStates["Admin"]) {
    setAssignToTerritory(false);
    setSelectedTerritories([]);
    return;
  }


  // ✅ NEW: Load from territories array (multi-territory)
  if (user.territories && Array.isArray(user.territories) && user.territories.length > 0) {
    const matched = territories.filter(t => 
      user.territories.some(ut => 
        (typeof ut === 'object' ? ut.id : ut) === t.id
      )
    );
    if (matched.length > 0) {
      setSelectedTerritories(matched);
      setAssignToTerritory(true);
      return;
    }
  }

  // ⚠️ LEGACY: Fallback to single territory_name
  if (user.territory_name) {
    const match = territories.find(
      (t) =>
        t.name &&
        t.name.toLowerCase() === user.territory_name.toLowerCase()
    );
    if (match) {
      setSelectedTerritories([match]);
      setAssignToTerritory(true);
      return;
    }
  }

  setAssignToTerritory(false);
}, [user, territories, permissionStates["Admin"]]);



  // Fetch groups
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await API.get("/groups/");
        setGroups(response.data?.results || response.data || []);
      } catch (error) {
        console.error("Error fetching groups:", error);
      }
    };
    fetchGroups();
  }, []);

  // Fetch users to check for existing admin
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await API.get("/users/");
        const usersList = response.data || [];
        setUsers(usersList);
        checkAdminExists(usersList);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [userId]);

  useEffect(() => {
    const fetchLeadCategories = async () => {
      if (!canManageLeadCategoryAccess) return;
      try {
        const response = await API.get("/lead-categories/");
        const rows = Array.isArray(response.data) ? response.data : (response.data?.results || []);
        setLeadCategories(rows);
      } catch (error) {
        console.error("Error fetching lead categories:", error);
      }
    };
    fetchLeadCategories();
  }, [canManageLeadCategoryAccess]);

  useEffect(() => {
    const fetchUserLeadCategoryAccess = async () => {
      if (!canManageLeadCategoryAccess || !userId) return;
      try {
        const response = await API.get(`/users/${userId}/lead-category-access/`);
        const ids = response.data?.allowed_category_ids || [];
        setSelectedLeadCategoryIds(ids);
      } catch (error) {
        console.error("Error fetching user lead category access:", error);
      }
    };
    fetchUserLeadCategoryAccess();
  }, [canManageLeadCategoryAccess, userId]);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error state if user not found
  if (!user) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" color="error">
          User not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
<Box sx={{ maxWidth: 1440, mx: "auto", width: "100%" }}>
      {/* ------------------------------------------------------------------------PROFILE HEADER */}
      <Card
        data-tour="edit-user-header"
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
        }}
      >
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems={{ xs: "flex-start", md: "center" }}
          flexDirection={{ xs: "column", md: "row" }}
          gap={{ xs: 2, md: 0 }}
        >
          <Box display="flex" alignItems="center" gap={2} width={{ xs: "100%", md: "auto" }}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: "primary.main", color: "#fff" }}>
              {getUserInitials(user, "U")}
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {user.full_name || user.username || "User"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {user.groups && user.groups.length > 0 
                  ? (Array.isArray(user.groups) 
                      ? user.groups.map(g => typeof g === 'object' ? g.name : g).join(", ")
                      : user.groups)
                  : "No role assigned"}
              </Typography>

              <Typography variant="caption" color="text.secondary">
                User ID: {userId}
              </Typography>
            </Box>
          </Box>

          <Stack 
            direction={{ xs: "column", md: "row" }} 
            spacing={1} 
            width={{ xs: "100%", md: "auto" }}
          >
            <Button 
              startIcon={<ArrowBackIcon />} 
              variant="outlined"
              onClick={() => navigate(-1)}
              fullWidth={isMobile}
              sx={{ whiteSpace: "nowrap" }}
            >
              Back
            </Button>
            {/* -----------------------------------------------Save and Delete buttons moved to bottom on mobile */}
            {!isMobile && (
              <>
                <DisabledButtonWrapper 
                  isDisabled={currentUserIsReadOnly}
                  tooltipText="Read-only mode: This action is disabled"
                >
                  <Button 
                    startIcon={<SaveIcon />} 
                    variant="outlined"
                    color="success"
                    onClick={handleSave}
                    disabled={currentUserIsReadOnly}
                    sx={{ 
                      whiteSpace: "nowrap",
                      opacity: currentUserIsReadOnly ? 0.6 : 1,
                      cursor: currentUserIsReadOnly ? 'not-allowed' : 'pointer',
                      visibility: 'visible',
                      display: 'inline-flex',
                    }}
                  >
                    Save
                  </Button>
                </DisabledButtonWrapper>
                <DisabledButtonWrapper 
                  isDisabled={currentUserIsReadOnly}
                  tooltipText="Read-only mode: This action is disabled"
                >
                  <Button 
                    startIcon={<DeleteIcon />} 
                    variant="outlined" 
                    color="error"
                    onClick={() => {
                      if (currentUserIsReadOnly) return;
                      setConfirmDelete(true);
                    }}
                    disabled={currentUserIsReadOnly}
                    sx={{ 
                      whiteSpace: "nowrap",
                      opacity: currentUserIsReadOnly ? 0.6 : 1,
                      cursor: currentUserIsReadOnly ? 'not-allowed' : 'pointer',
                      visibility: 'visible',
                      display: 'inline-flex',
                    }}
                  >
                    Delete User
                  </Button>
                </DisabledButtonWrapper>
              </>
            )}
          </Stack>
        </Box>
      </Card>

      {/*----------------------------------------------------------------------------------------------------- MAIN GRID */}
      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr 1fr" }}
        gap={3}
      >
        {/* -------------------------------------------------------------PROFILE INFO */}
        <Card sx={{ borderRadius: 3 }} data-tour="edit-user-profile-info">
          <CardContent>
            <Box display="flex" justifyContent="space-between">
              <Typography fontWeight={700}>Profile Information</Typography>
              <DisabledButtonWrapper 
                isDisabled={currentUserIsReadOnly}
                tooltipText="Read-only mode: This action is disabled"
              >
                <IconButton 
                  size="small" 
                  onClick={() => {
                    if (currentUserIsReadOnly) return;
                    setIsProfileEditMode(!isProfileEditMode);
                  }}
                  disabled={currentUserIsReadOnly}
                  sx={{
                    opacity: currentUserIsReadOnly ? 0.6 : 1,
                    cursor: currentUserIsReadOnly ? 'not-allowed' : 'pointer',
                    visibility: 'visible',
                    display: 'inline-flex',
                    color: currentUserIsReadOnly ? 'text.disabled' : 'primary.main',
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </DisabledButtonWrapper>
            </Box>

            <Typography variant="body2" color="text.secondary" mt={2}>
             Short Bio:
            </Typography>

            <Divider sx={{ my: 2 }} />

            {isProfileEditMode ? (
              <>
                <TextField
                  label="Full Name"
                  value={profileData.fullName}
                  onChange={(e) => {
                    if (currentUserIsReadOnly) return;
                    setProfileData({ ...profileData, fullName: e.target.value });
                  }}
                  disabled={currentUserIsReadOnly}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Username"
                  value={profileData.username}
                  autoComplete="off"
                  inputProps={{ name: "edit_user_username", autoComplete: "off" }}
                  onChange={(e) => {
                    if (currentUserIsReadOnly) return;
                    setProfileData({ ...profileData, username: e.target.value });
                  }}
                  disabled={currentUserIsReadOnly}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  value={profileData.password}
                  autoComplete="new-password"
                  inputProps={{ name: "edit_user_password", autoComplete: "new-password" }}
                  onChange={(e) => {
                    if (currentUserIsReadOnly) return;
                    setProfileData({ ...profileData, password: e.target.value });
                  }}
                  disabled={currentUserIsReadOnly}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  InputProps={{
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (currentUserIsReadOnly) return;
                          setShowPassword(!showPassword);
                        }}
                        disabled={currentUserIsReadOnly}
                        edge="end"
                        sx={{
                          opacity: currentUserIsReadOnly ? 0.6 : 1,
                          cursor: currentUserIsReadOnly ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    ),
                  }}
                />
                <TextField
                  label="Mobile"
                  value={profileData.mobile}
                  onChange={(e) => setProfileData({ ...profileData, mobile: e.target.value })}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Email"
                  type="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Location"
                  value={profileData.location}
                  onChange={(e) => setProfileData({ ...profileData, location: e.target.value })}
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                />
              </>
            ) : (
              <>
                <ProfileRow label="Full Name" value={profileData.fullName} />
                <ProfileRow label="Username" value={profileData.username} />
                <PasswordRow 
                  label="Password" 
                  value={profileData.password} 
                  showPassword={showPassword}
                  onToggle={() => setShowPassword(!showPassword)}
                />
                <ProfileRow label="Mobile" value={profileData.mobile} />
                <ProfileRow label="Email" value={profileData.email} />
                <ProfileRow label="Location" value={profileData.location} />
              </>
            )}
          </CardContent>
        </Card>

        {/* --------------------------------------------------PLATFORM SETTINGS */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={2}>
              Platform Settings
            </Typography>

            <Box data-tour="edit-user-platform-account">
            <Typography variant="caption" color="text.secondary">
              ACCOUNT
            </Typography>

            <SettingRow 
              label="Active" 
              checked={targetUserIsAdmin ? true : platformSettings.isActive}
              onChange={(checked) => {
                if (currentUserIsReadOnly || targetUserIsAdmin) return;
                setPlatformSettings(prev => ({ ...prev, isActive: checked }));
              }}
              disabled={currentUserIsReadOnly || targetUserIsAdmin}
              tooltipText={targetUserIsAdmin ? adminSettingsLockedMessage : ""}
            />
            
            {/* ✅ Read Only toggle - only show for Admin/Sales Director */}
            {can.editAnyUser && (
              <>
                <SettingRow 
                  label="Read Only" 
                  checked={permissionStates["Admin"] ? false : isReadOnly}
                  onChange={handleReadOnlyChange}
                  disabled={currentUserIsReadOnly || permissionStates["Admin"]}
                  tooltipText={targetUserIsAdmin ? adminSettingsLockedMessage : ""}
                />
                {permissionStates["Admin"] && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 3, display: "block", mt: 0.5 }}>
                    Admin accounts cannot be set to read-only
                  </Typography>
                )}
                {!permissionStates["Admin"] && isReadOnly && (
                  <Typography variant="caption" color="warning.main" sx={{ ml: 3, display: "block", mt: 0.5 }}>
                    User can view but cannot create, update, or delete records
                  </Typography>
                )}
                <SettingRow
                  label="Can view additional notes"
                  checked={targetUserIsAdmin ? true : canViewAdditionalNotes}
                  onChange={handleCanViewAdditionalNotesChange}
                  disabled={currentUserIsReadOnly || targetUserIsAdmin}
                  tooltipText={targetUserIsAdmin ? adminSettingsLockedMessage : ""}
                />
                {!canViewAdditionalNotes && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 3, display: "block", mt: 0.5 }}>
                    Lead additional notes are hidden for this user
                  </Typography>
                )}
              </>
            )}
            <SettingRow 
              label="Allow System emails" 
              checked={targetUserIsAdmin ? true : platformSettings.sendSystemEmails}
              onChange={(checked) => {
                if (currentUserIsReadOnly || targetUserIsAdmin) return;
                setPlatformSettings(prev => ({ ...prev, sendSystemEmails: checked }));
              }}
              disabled={currentUserIsReadOnly || targetUserIsAdmin}
              tooltipText={targetUserIsAdmin ? adminSettingsLockedMessage : ""}
            />

            <SettingRow 
              label="Allow System SMS" 
              checked={platformSettings.sendSystemSMS}
              onChange={(checked) => {
                if (currentUserIsReadOnly) return;
                setPlatformSettings(prev => ({ ...prev, sendSystemSMS: checked }));
              }}
              disabled={currentUserIsReadOnly}
            />

            </Box>

            <Divider sx={{ my: 2 }} />

            <Box data-tour="edit-user-territory-settings">
            <Typography variant="caption" color="text.secondary">
              TERRITORY SETTING
            </Typography>

            {/* ✅ Display territory validation error */}
            {territoryError && (
              <Alert 
                severity="error" 
                sx={{ mt: 1, mb: 1 }}
                id="territory-error"
                onClose={() => setTerritoryError("")}
              >
                {territoryError}
              </Alert>
            )}

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              my={1}
              title={targetUserIsAdmin ? adminSettingsLockedMessage : ""}
            >
              <Typography variant="body2">Assign to territory</Typography>
              <Switch 
                checked={assignToTerritory} 
                disabled={currentUserIsReadOnly || targetUserIsAdmin}
                onChange={(e) => {
                  if (currentUserIsReadOnly || targetUserIsAdmin) return;
                  const newValue = e.target.checked;
                  setAssignToTerritory(newValue);
                  // ✅ Clear error when toggle changes
                  setTerritoryError("");
                  // If turning OFF, clear selected territories
                  if (!newValue) {
                    setSelectedTerritories([]);
                  } else if (user && user.territory_id && selectedTerritories.length === 0) {
                    // If turning ON and user has a territory, restore it
                    const userTerritory = territories.find(t => t.id === user.territory_id);
                    if (userTerritory) {
                      setSelectedTerritories([userTerritory]);
                    }
                  }
                }}
              />
            </Box>

            <Box my={2} title={targetUserIsAdmin ? adminSettingsLockedMessage : ""}>
              <Autocomplete
                multiple
                disabled={currentUserIsReadOnly || targetUserIsAdmin || !assignToTerritory}
                options={territories}
                getOptionLabel={(option) => option.name || ""}
                value={selectedTerritories}
                onChange={(event, newValue) => {
                  if (currentUserIsReadOnly || targetUserIsAdmin) return;
                  setSelectedTerritories(newValue);
                  // ✅ Clear error when territories are selected
                  if (newValue.length > 0) {
                    setTerritoryError("");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Territories"
                    placeholder="Choose territories"
                    error={!!territoryError}
                    disabled={currentUserIsReadOnly || targetUserIsAdmin || !assignToTerritory}
                    size="small"
                    sx={{
                      "& .MuiInputBase-root": {
                        fontSize: "0.875rem", // 14px, matches body2
                      },
                      "& .MuiInputLabel-root": {
                        fontSize: "0.875rem",
                      },
                      "& .MuiChip-root": {
                        fontSize: "0.875rem",
                        height: "24px",
                      },
                    }}
                  />
                )}
                sx={{
                  "& .MuiAutocomplete-tag": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Box>

            </Box>

            {canManageLeadCategoryAccess && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  LEAD CATEGORY VISIBILITY
                </Typography>
                <Box my={2} data-tour="edit-user-lead-categories" title={targetUserIsAdmin ? adminSettingsLockedMessage : ""}>
                  <Autocomplete
                    multiple
                    disabled={currentUserIsReadOnly || targetUserIsAdmin}
                    options={leadCategories}
                    value={leadCategories.filter((c) => selectedLeadCategoryIds.includes(c.id))}
                    getOptionLabel={(option) =>
                      displayLeadCategoryLabel(option.label || option.key || "")
                    }
                    onChange={(event, newValue) => {
                      if (currentUserIsReadOnly || targetUserIsAdmin) return;
                      setSelectedLeadCategoryIds(newValue.map((v) => v.id));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Allowed Lead Categories"
                        placeholder="Select categories"
                        disabled={currentUserIsReadOnly || targetUserIsAdmin}
                        size="small"
                        helperText={
                          targetUserIsAdmin
                            ? "Admin users use default lead category visibility."
                            : "If none are selected, this user defaults to Cold Lead only."
                        }
                      />
                    )}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        {/* -------------------------------------------------PERMISSIONS */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography fontWeight={700} mb={2}>
              Permissions
            </Typography>

            <Box data-tour="edit-user-permissions">
            <SettingRow 
              label="Admin" 
              checked={permissionStates["Admin"]}
              onChange={(checked) => handlePermissionChange("Admin", checked)}
              disabled={currentUserIsReadOnly || targetUserIsAdmin}
            />
            <SettingRow 
              label="Sales Director" 
              checked={permissionStates["Sales Director"]}
              onChange={(checked) => handlePermissionChange("Sales Director", checked)}
              disabled={currentUserIsReadOnly || targetUserIsAdmin}
            />
            <SettingRow 
              label="Manager" 
              checked={permissionStates["Manager"]}
              onChange={(checked) => handlePermissionChange("Manager", checked)}
              disabled={currentUserIsReadOnly || targetUserIsAdmin}
            />
            <SettingRow 
              label="Sales Rep" 
              checked={permissionStates["Sales Rep"]}
              onChange={(checked) => handlePermissionChange("Sales Rep", checked)}
              disabled={currentUserIsReadOnly || targetUserIsAdmin}
            />
            {targetUserIsAdmin && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Admin role is protected and cannot be changed here.
              </Typography>
            )}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              FILLQUICK USER CREDENTIALS
            </Typography>
            <Box my={2} data-tour="edit-user-fillquick">
              <Stack spacing={2}>
                <TextField
                  label="FillQuick User ID"
                  value={fillQuickUserId}
                  autoComplete="off"
                  inputProps={{ name: "fillquick_user_id", autoComplete: "off" }}
                  onChange={(e) => {
                    if (currentUserIsReadOnly) return;
                    setFillQuickUserId(e.target.value);
                  }}
                  disabled={currentUserIsReadOnly}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="FillQuick API Key"
                  type="password"
                  autoComplete="new-password"
                  inputProps={{ name: "fillquick_api_key", autoComplete: "new-password" }}
                  value={
                    showStoredFillQuickMask && !fillQuickApiKey
                      ? "****************"
                      : fillQuickApiKey
                  }
                  onFocus={() => {
                    if (currentUserIsReadOnly) return;
                    if (showStoredFillQuickMask) {
                      setShowStoredFillQuickMask(false);
                      setFillQuickApiKey("");
                    }
                  }}
                  onBlur={() => {
                    if (!fillQuickApiKey.trim() && hasStoredFillQuickApiKey) {
                      setShowStoredFillQuickMask(true);
                    }
                  }}
                  onChange={(e) => {
                    if (currentUserIsReadOnly) return;
                    if (showStoredFillQuickMask) {
                      setShowStoredFillQuickMask(false);
                    }
                    setFillQuickApiKey(e.target.value);
                  }}
                  disabled={currentUserIsReadOnly}
                  fullWidth
                  size="small"
                  placeholder="Leave blank to keep existing key"
                  helperText="If user credentials are missing, FillQuick push falls back to company credentials."
                />
              </Stack>
            </Box>
          </CardContent>
        </Card>
</Box>
      </Box>

      {/* ------------------------------------------------------------------------Mobile Save and Delete Buttons - Bottom of Page */}
      {isMobile && (
        <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          <DisabledButtonWrapper 
            isDisabled={currentUserIsReadOnly}
            tooltipText="Read-only mode: This action is disabled"
          >
            <Button 
              startIcon={<SaveIcon />} 
              variant="outlined"
              color="success"
              onClick={handleSave}
              disabled={currentUserIsReadOnly}
              fullWidth
              sx={{ 
                whiteSpace: "nowrap",
                opacity: currentUserIsReadOnly ? 0.6 : 1,
                cursor: currentUserIsReadOnly ? 'not-allowed' : 'pointer',
                visibility: 'visible',
                display: 'inline-flex',
              }}
            >
              Save
            </Button>
          </DisabledButtonWrapper>
          <DisabledButtonWrapper 
            isDisabled={currentUserIsReadOnly}
            tooltipText="Read-only mode: This action is disabled"
          >
            <Button 
              startIcon={<DeleteIcon />} 
              variant="outlined" 
              color="error"
              onClick={() => {
                if (currentUserIsReadOnly) return;
                setConfirmDelete(true);
              }}
              disabled={currentUserIsReadOnly}
              fullWidth
              sx={{ 
                whiteSpace: "nowrap",
                opacity: currentUserIsReadOnly ? 0.6 : 1,
                cursor: currentUserIsReadOnly ? 'not-allowed' : 'pointer',
                visibility: 'visible',
                display: 'inline-flex',
              }}
            >
              Delete User
            </Button>
          </DisabledButtonWrapper>
        </Box>
      )}

      {/* ----------------------------------------------------------Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDeleteUser}
        title="Delete User"
        message="Are you sure you want to delete this user? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        itemName={user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.username : ""}
        severity="error"
      />

      {/* -----------------------------------------------------------------------Sales Director Confirmation Dialog */}
      <ConfirmDialog
        open={confirmSalesDirector}
        onClose={() => {
          setConfirmSalesDirector(false);
          setPendingSalesDirectorChange(false);
        }}
        onConfirm={handleConfirmSalesDirector}
        title="Confirm Sales Director Role"
        message="Are you sure you want to make this user a Sales Director?"
        confirmText="Yes, Confirm"
        cancelText="Cancel"
      />

      {/* ---------------------------------------------------------------------------------------Admin Warning Dialog */}
      <ConfirmDialog
        open={showAdminWarning}
        onClose={() => setShowAdminWarning(false)}
        onConfirm={() => setShowAdminWarning(false)}
        title="Admin Role Already Assigned"
        message="ADMIN role is already assigned. You cannot have more than one admin."
        confirmText="OK"
        cancelText={null}
        severity="warning"
      />

      <ConfirmDialog
        open={showProtectedAdminRoleWarning}
        onClose={() => setShowProtectedAdminRoleWarning(false)}
        onConfirm={() => setShowProtectedAdminRoleWarning(false)}
        title="Admin role is protected"
        message="This admin user’s role can’t be changed here. Contact platform support if you need admin changes."
        confirmText="OK"
        cancelText={null}
        severity="info"
      />
    </Box>
  );
}

/* -------- SMALL COMPONENTS -------- */

function SettingRow({ label, checked, onChange, disabled = false, tooltipText = "" }) {
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      my={1}
      title={tooltipText}
    >
      <Typography variant="body2">{label}</Typography>
      <Switch 
        checked={checked || false} 
        disabled={disabled}
        onChange={(e) => {
          if (disabled) return;
          onChange && onChange(e.target.checked);
        }}
        sx={{
          opacity: disabled ? 0.6 : 1,
        }}
      />
    </Box>
  );
}

function ProfileRow({ label, value }) {
  return (
    <Typography
  variant="body1"
  sx={{ fontSize: "0.95rem" }}  // 👈 subtle bump
  mb={1}
>
      <strong>{label}:</strong>{" "}
      <Typography
        component="span"
        sx={{
          color: (theme) =>
            theme.palette.mode === "dark"
              ? "rgba(226,232,240,0.95)"
              : "text.secondary",
        }}
      >
        {value || "—"}
      </Typography>
    </Typography>
  );
}


function PasswordRow({ label, value, showPassword, onToggle }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
      <Typography
  variant="body1"
  sx={{ fontSize: "0.95rem" }}
>

        <strong>{label}:</strong>{" "}
        <Typography
          component="span"
          sx={{
            color: (theme) =>
              theme.palette.mode === "dark"
                ? "rgba(226,232,240,0.95)"
                : "text.secondary",
          }}
        >
          {showPassword ? value || "—" : "••••••••"}
        </Typography>
      </Typography>

      <IconButton
        size="small"
        onClick={onToggle}
        sx={{ ml: 1, color: "text.secondary" }}
      >
        {showPassword ? (
          <VisibilityOffIcon fontSize="small" />
        ) : (
          <VisibilityIcon fontSize="small" />
        )}
      </IconButton>
    </Box>
  );
}


function Conversation({ name, message }) {
  return (
    <Box display="flex" alignItems="center" gap={2} mb={2}>
      <Avatar sx={{ bgcolor: "primary.main", color: "#fff" }}>
        {getUserInitials(name, "U")}
      </Avatar>
      <Box flex={1}>
        <Typography fontWeight={600}>{name}</Typography>
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Box>
      <Button size="small">Reply</Button>
    </Box>
  );
}
